/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import BezierEasing from 'bezier-easing';
import clamp from 'lodash/clamp';

import Accordion from 'components/Accordion/Accordion';

import styles from './ExpandableText.module.scss';

const MIN_DURATION = 500;
const MAX_DURATION = 750;
// const ease = new BezierEasing(0.5, 0.02, 0.66, 1.0);
const ease = new BezierEasing(0.5, 0, 0.42, 1);

/**
 * This is the ExpandableText component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * @return {JSX}
 */
function ExpandableText({
  className,
  body,
  expanded,
  items,
  multiple,
  children,
  calcMargins,
  shouldFadeIn,
}) {
  const [contentHeight, setContentHeight] = useState(null);
  const [progress, setProgress] = useState(expanded ? 1 : 0);
  const [targetProgress, setTargetProgress] = useState(expanded ? 1 : 0);
  const [active, setActive] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const [lastFrameAt, setLastFrameAt] = useState(null);
  const [duration, setDuration] = useState(MIN_DURATION);

  const contentRef = useRef(null);
  const bodyRef = useRef(null);
  const rafId = useRef(null);

  useEffect(() => {
    setTargetProgress(expanded ? 1 : 0);
    if (contentRef.current) {
      // Calculate expandedHeight based on height of content, top/bottom margin of current ref + top/margin of potential first-/last-child of current ref
      const rect = contentRef.current.getBoundingClientRect();

      let marginTop = 0;
      let marginBottom = 0;

      if (calcMargins && bodyRef.current) {
        const style = window.getComputedStyle(bodyRef.current);

        marginTop = parseInt(style.marginTop, 10);
        marginBottom = parseInt(style.marginBottom, 10);

        if (bodyRef.current.firstChild instanceof Element) {
          marginTop += parseInt(
            window.getComputedStyle(bodyRef.current.firstChild).marginTop,
            10
          );
        }
        if (bodyRef.current.lastChild instanceof Element) {
          marginBottom += parseInt(
            window.getComputedStyle(bodyRef.current.lastChild).marginBottom,
            10
          );
        }
      }

      let nextContentHeight =
        Math.round(rect.height * 1000) / 1000 + marginTop + marginBottom;

      setContentHeight(nextContentHeight);

      // Clamp pixels per ms speed
      const clampedPpms = clamp(nextContentHeight / MIN_DURATION, 0.8, 1.1);
      // Clamp duration
      setDuration(
        clamp(nextContentHeight / clampedPpms, MIN_DURATION, MAX_DURATION)
      );
    }
  }, [expanded]);

  useEffect(() => {
    if (!active && progress !== targetProgress) {
      setActive(true);
      setLastFrameAt(null);
    } else if (active && progress === targetProgress) {
      setActive(false);
      setLastFrameAt(null);
    }
  }, [progress, targetProgress]);

  // Update progress via RAF when active
  useEffect(() => {
    if (active) {
      if (progress !== targetProgress) {
        let previousFrameAt = lastFrameAt || Date.now();

        rafId.current = requestAnimationFrame(() => {
          const now = Date.now();
          const diff = now - previousFrameAt;
          const progressed = diff / duration;

          const nextProgress = clamp(
            progress + (targetProgress === 1 ? progressed : -progressed),
            0,
            1
          );

          setProgress(nextProgress);
          setLastFrameAt(now);
          setAnimationStep(animationStep + 1);
        });
      }
    }

    return () => {
      cancelAnimationFrame(rafId.current);
    };
  }, [active, progress, targetProgress, animationStep]);

  const easedProgress = ease(progress);

  const style = {
    height: easedProgress < 1 ? easedProgress * contentHeight : 'auto',
  };

  return (
    <div
      className={classNames(
        styles.main,
        { [styles.expanded]: expanded },
        className
      )}
      style={style}
    >
      <div
        ref={contentRef}
        className={classNames(styles.content, {
          [styles.contentFadeIn]: shouldFadeIn,
        })}
        style={{ visibility: easedProgress !== 0 ? 'visible' : 'hidden' }}
      >
        {body && (
          <div
            ref={bodyRef}
            className="text-content"
            dangerouslySetInnerHTML={{ __html: body }}
          />
        )}
        {items && <Accordion items={items} nested multiple={multiple} />}
        {!items && !body && <>{children}</>}
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
ExpandableText.propTypes = {
  className: PropTypes.string,
  body: PropTypes.string,
  expanded: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape),
  multiple: PropTypes.bool,
  calcMargins: PropTypes.bool,
  shouldFadeIn: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
ExpandableText.defaultProps = {
  className: null,
  body: null,
  expanded: false,
  items: null,
  multiple: true,
  calcMargins: true,
  shouldFadeIn: false,
  children: null,
};

export default ExpandableText;
